import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { usePomodoro, TIMER_MODES, MODE_LABELS } from '../context/PomodoroContext';
import { getColors, getModalStyle } from '../theme';
import { X } from 'lucide-react';
import { InkBox } from './Notebook';

/**
 * Focus timer panel for the Tasks page, drawn as a notebook block:
 * mode tabs, a large mono clock, a hairline progress bar and ruled rows.
 * State lives in the global PomodoroContext so it keeps running across pages.
 */
// `sidebar`: on large screens the timer sits in a narrow column, so the clock and controls stack.
const PomodoroTimer = ({ tasks = [], sidebar = false }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const pomodoro = usePomodoro();

  const [showSettings, setShowSettings] = useState(false);

  const selectedTask = pomodoro.activeTaskId
    ? tasks.find(t => t.id === pomodoro.activeTaskId)
    : null;
  const focusTitle = selectedTask?.title || pomodoro.activeTaskTitle;

  const handleStart = () => {
    pomodoro.requestNotificationPermission();
    pomodoro.start();
  };

  if (!pomodoro.settingsLoaded) {
    return null;
  }

  const perSet = pomodoro.settings.sessionsBeforeLongBreak;
  const doneInSet = pomodoro.sessionsCompleted > 0 && pomodoro.sessionsCompleted % perSet === 0
    ? perSet
    : pomodoro.sessionsCompleted % perSet;
  const rule = `1px solid ${colors.border}`;
  const textButton = 'font-mono text-xs min-h-[36px] px-1 hover:underline underline-offset-4';

  return (
    <>
      <div style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
        {/* Mode tabs + settings */}
        <div className="flex items-center gap-4 sm:gap-5 pt-2" style={{ borderBottom: rule }}>
          {Object.values(TIMER_MODES).map(m => {
            const isActive = pomodoro.mode === m;
            return (
              <button
                key={m}
                onClick={() => pomodoro.switchMode(m)}
                aria-pressed={isActive}
                className="font-mono text-xs py-2 -mb-px transition-colors duration-150"
                style={{
                  color: isActive ? colors.textPrimary : colors.textSecondary,
                  borderBottom: `1.5px solid ${isActive ? colors.textPrimary : 'transparent'}`,
                }}
              >
                {MODE_LABELS[m]}
              </button>
            );
          })}
          <span className="flex-1" />
          <button onClick={() => setShowSettings(true)} className="font-mono text-xs py-2 hover:underline underline-offset-4" style={{ color: colors.textSecondary }}>
            Settings
          </button>
        </div>

        {/* Clock + controls */}
        <div className={`flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-10 pt-6 pb-5 ${sidebar ? 'lg:flex-col lg:items-stretch lg:gap-5' : ''}`}>
          <div className="min-w-0">
            <p
              className={`font-mono font-light tabular-nums leading-none text-[64px] sm:text-[80px] ${sidebar ? 'lg:text-[64px]' : ''}`}
              style={{ color: colors.textPrimary, letterSpacing: '-0.05em' }}
              aria-live="off"
            >
              {pomodoro.formattedTime}
            </p>
            <div className={`mt-4 h-[2px] w-full sm:w-[300px] ${sidebar ? 'lg:w-full' : ''}`} style={{ backgroundColor: colors.border }}>
              <div className="h-full transition-[width] duration-300 ease-linear" style={{ width: `${pomodoro.progress * 100}%`, backgroundColor: colors.textPrimary }} />
            </div>
            <div className="flex items-center gap-1.5 mt-3 font-mono text-xs" style={{ color: colors.textSecondary }}>
              {Array.from({ length: perSet }, (_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className="w-2 h-2 transition-colors duration-200"
                  style={{ border: `1px solid ${colors.textPrimary}`, backgroundColor: i < doneInSet ? colors.textPrimary : 'transparent' }}
                />
              ))}
              <span className="ml-1.5">{doneInSet} of {perSet} · {pomodoro.todayFocusMinutes}m today</span>
            </div>
          </div>

          <div className={`flex items-center gap-4 sm:ml-auto ${sidebar ? 'lg:ml-0' : ''}`}>
            <button
              onClick={pomodoro.isRunning ? pomodoro.pause : handleStart}
              className="min-w-[96px] min-h-[40px] px-5 text-sm font-medium transition-opacity hover:opacity-85"
              style={{ backgroundColor: colors.textPrimary, color: colors.onAccent }}
            >
              {pomodoro.isRunning ? 'Pause' : pomodoro.isActive ? 'Resume' : 'Start'}
            </button>
            <button onClick={pomodoro.reset} className={textButton} style={{ color: colors.textSecondary }}>Reset</button>
            <button onClick={pomodoro.skip} className={textButton} style={{ color: colors.textSecondary }}>Skip</button>
          </div>
        </div>

        {/* The task being focused on */}
        <div className="flex items-center gap-3 min-h-[48px]" style={{ borderTop: rule, borderBottom: rule }}>
          <span className="font-mono text-xs w-[50px] sm:w-[64px] self-stretch flex items-center flex-shrink-0" style={{ color: colors.textSecondary, borderRight: rule }}>
            On
          </span>
          {focusTitle ? (
            <>
              <span className="flex-1 min-w-0 truncate text-[15px]" style={{ color: colors.textPrimary }}>{focusTitle}</span>
              <button
                onClick={() => pomodoro.clearTask()}
                className="p-2 transition-opacity hover:opacity-60"
                style={{ color: colors.textMuted }}
                aria-label="Stop focusing on this task"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <span className="flex-1 min-w-0 text-sm" style={{ color: colors.textMuted }}>
              Nothing yet. Use the timer button on a task to focus on it.
            </span>
          )}
        </div>
      </div>

      {showSettings && createPortal(
        <PomodoroSettingsModal
          colors={colors}
          settings={pomodoro.settings}
          onSave={(newSettings) => {
            pomodoro.updateSettings(newSettings);
            setShowSettings(false);
          }}
          onClose={() => setShowSettings(false)}
        />,
        document.body
      )}
    </>
  );
};

const DURATIONS = [
  { key: 'workDuration', label: 'Focus', unit: 'min', min: 1, max: 120, fallback: 25 },
  { key: 'shortBreakDuration', label: 'Short break', unit: 'min', min: 1, max: 30, fallback: 5 },
  { key: 'longBreakDuration', label: 'Long break', unit: 'min', min: 1, max: 60, fallback: 15 },
  { key: 'sessionsBeforeLongBreak', label: 'Sessions before a long break', unit: '', min: 1, max: 10, fallback: 4 },
];

const TOGGLES = [
  { key: 'autoStartBreaks', label: 'Start breaks automatically' },
  { key: 'autoStartWork', label: 'Start focus after a break' },
  { key: 'soundEnabled', label: 'Sound' },
  { key: 'notificationsEnabled', label: 'Browser notifications' },
];

const PomodoroSettingsModal = ({ colors, settings, onSave, onClose }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const rule = `1px solid ${colors.border}`;

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const groupHead = (title) => (
    <h4 className="text-[15px] font-semibold tracking-tight pb-2 mt-6 first:mt-0" style={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.textPrimary}` }}>
      {title}
    </h4>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 animate-fade-in" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pomodoro-settings-title"
        className="w-full sm:max-w-md animate-scale-up max-h-[92vh] flex flex-col"
        style={getModalStyle(colors)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-4">
          <div>
            <p className="font-mono text-xs mb-1.5" style={{ color: colors.textSecondary }}>Focus timer</p>
            <h3 id="pomodoro-settings-title" className="text-2xl font-semibold" style={{ color: colors.textPrimary, letterSpacing: '-0.03em' }}>
              Settings
            </h3>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 transition-opacity hover:opacity-60" style={{ color: colors.textMuted }} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pb-2 overflow-y-auto">
          {groupHead('Durations')}
          {DURATIONS.map(({ key, label, unit, min, max, fallback }) => (
            <label key={key} className="flex items-center justify-between gap-4 min-h-[48px]" style={{ borderBottom: rule }}>
              <span className="text-sm" style={{ color: colors.textPrimary }}>{label}</span>
              <span className="flex items-baseline gap-1.5">
                <input
                  type="number"
                  inputMode="numeric"
                  min={min}
                  max={max}
                  value={localSettings[key]}
                  onChange={(e) => handleChange(key, parseInt(e.target.value, 10) || fallback)}
                  className="w-14 bg-transparent text-right font-mono text-base sm:text-sm tabular-nums outline-none py-1"
                  style={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.borderStrong}` }}
                />
                {unit && <span className="font-mono text-xs w-6" style={{ color: colors.textMuted }}>{unit}</span>}
                {!unit && <span className="w-6" />}
              </span>
            </label>
          ))}

          {groupHead('Behaviour')}
          {TOGGLES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleChange(key, !localSettings[key])}
              aria-pressed={!!localSettings[key]}
              className="w-full flex items-center gap-3 min-h-[48px] text-left notebook-row-hover"
              style={{ borderBottom: rule }}
            >
              <InkBox checked={!!localSettings[key]} size={16} />
              <span className="text-sm" style={{ color: colors.textPrimary }}>{label}</span>
            </button>
          ))}
        </div>

        <div className="flex gap-3 p-5">
          <button
            onClick={onClose}
            className="flex-1 min-h-[44px] text-sm font-medium transition-opacity hover:opacity-70"
            style={{ color: colors.textPrimary, border: `1px solid ${colors.borderStrong}` }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(localSettings)}
            className="flex-1 min-h-[44px] text-sm font-medium transition-opacity hover:opacity-85"
            style={{ backgroundColor: colors.textPrimary, color: colors.onAccent }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default PomodoroTimer;
